/**
 * Elapsed-time catch-up.
 *
 * Heat is deliberately not simulated while away. No meltdown ever happens
 * offline - that would punish the player for closing the tab. Output is
 * computed at a safe idle (clock 100, heat 0) and scaled by the offline rate.
 */

import Decimal from 'break_infinity.js';
import { OFFLINE_MODAL_MIN_SECONDS, OFFLINE_SKEW_LIMIT_SECONDS } from '../data/balance.ts';
import { getModifiers, idleTPS, type Modifiers } from './formulas.ts';
import type { GameState } from './state.ts';

export interface OfflineReport {
  /** Seconds actually credited, after the cap. */
  seconds: number;
  /** Seconds that really elapsed, before the cap. Shown to the player. */
  elapsedSeconds: number;
  gain: Decimal;
  /** Whether the gap was long enough to be worth a modal. */
  worthShowing: boolean;
}

/**
 * Works out what the rig produced while the tab was closed.
 * Pure: apply it with collectOffline().
 */
export function computeOffline(
  state: GameState,
  now: number,
  mods: Modifiers = getModifiers(state),
): OfflineReport {
  const elapsed = (now - state.lastSavedAt) / 1000;
  const none: OfflineReport = {
    seconds: 0,
    elapsedSeconds: 0,
    gain: new Decimal(0),
    worthShowing: false,
  };

  // A negative or absurd gap is a clock change, not time the player was away.
  if (!Number.isFinite(elapsed) || elapsed <= 0 || elapsed > OFFLINE_SKEW_LIMIT_SECONDS) {
    return none;
  }

  const credited = Math.min(elapsed, mods.offlineCapSeconds);
  const gain = idleTPS(state, mods).mul(credited).mul(mods.offlineRate);

  return {
    seconds: credited,
    elapsedSeconds: elapsed,
    gain,
    worthShowing: elapsed >= OFFLINE_MODAL_MIN_SECONDS && gain.gt(0),
  };
}

/** Banks an offline report. Safe to call with a zero report. */
export function collectOffline(state: GameState, report: OfflineReport): void {
  if (!report.gain.gt(0)) return;
  state.thoughts = state.thoughts.add(report.gain);
  state.thoughtsThisRun = state.thoughtsThisRun.add(report.gain);
  state.stats.lifetimeThoughts = state.stats.lifetimeThoughts.add(report.gain);
  if (state.thoughtsThisRun.gt(state.stats.bestRunThoughts)) {
    state.stats.bestRunThoughts = state.thoughtsThisRun;
  }
}
